import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import MenuBar from "../MenuBar";
import { apiFetch } from "../api";

const THEMES = {
  dark: {
    page: { bg: "#0a0a0a", color: "#fff" },
    card: { bg: "rgba(255,255,255,0.03)", border: "rgba(255,255,255,0.08)" },
    cardHeader: { bg: "rgba(230,255,62,0.07)", border: "rgba(230,255,62,0.15)", color: "#e6ff3e" },
    row: { border: "rgba(255,255,255,0.05)" },
    cellLabel: { color: "#64748b" },
    input: { bg: "rgba(255,255,255,0.05)", border: "rgba(255,255,255,0.1)", color: "#fff" },
    inputReadonly: { bg: "rgba(255,255,255,0.02)", border: "rgba(255,255,255,0.06)", color: "#94a3b8" },
    select: { bg: "rgba(255,255,255,0.05)", border: "rgba(255,255,255,0.1)", color: "#fff" },
    actionsPanel: { bg: "rgba(0,0,0,0.2)", border: "rgba(255,255,255,0.06)" },
    sectionTitle: { color: "#fff" },
    toggleBtn: { bg: "rgba(255,255,255,0.06)", border: "rgba(255,255,255,0.12)", color: "#fff" },
    accent: "#e6ff3e",
    accentText: "#000",
  },
  light: {
    page: { bg: "#f1f5f9", color: "#0f172a" },
    card: { bg: "#fff", border: "#e2e8f0" },
    cardHeader: { bg: "#f8fafc", border: "#e2e8f0", color: "#475569" },
    row: { border: "#f1f5f9" },
    cellLabel: { color: "#94a3b8" },
    input: { bg: "#f8fafc", border: "#e2e8f0", color: "#0f172a" },
    inputReadonly: { bg: "#f1f5f9", border: "#e2e8f0", color: "#64748b" },
    select: { bg: "#f8fafc", border: "#e2e8f0", color: "#0f172a" },
    actionsPanel: { bg: "#f8fafc", border: "#e2e8f0" },
    sectionTitle: { color: "#0f172a" },
    toggleBtn: { bg: "#fff", border: "#e2e8f0", color: "#334155" },
    accent: "#1d4ed8",
    accentText: "#fff",
  },
};

function injectStyles(mode) {
  let el = document.getElementById("cresine-dynamic-styles");
  if (!el) {
    el = document.createElement("style");
    el.id = "cresine-dynamic-styles";
    document.head.appendChild(el);
  }
  const lime = mode === "dark" ? "rgba(230,255,62,0.5)" : "#1d4ed8";
  const shadow = mode === "dark" ? "rgba(230,255,62,0.1)" : "rgba(29,78,216,0.1)";
  el.innerHTML = `
    .cresine-input:focus, .cresine-select:focus {
      border-color: ${lime} !important;
      box-shadow: 0 0 0 3px ${shadow} !important;
      outline: none;
    }
    @media (min-width: 640px) { .cresine-row { grid-template-columns: 200px 1fr !important; } }
  `;
}

function Row({ label, children, T }) {
  return (
    <div className="cresine-row" style={{ display: "grid", gridTemplateColumns: "1fr", alignItems: "center", borderBottom: `1px solid ${T.row.border}`, minHeight: 48 }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: T.cellLabel.color, padding: "8px 4px" }}>{label}</div>
      <div style={{ padding: "4px 0 8px" }}>{children}</div>
    </div>
  );
}

export default function ControleResineForm() {
  const location = useLocation();
  const today = new Date().toISOString().split("T")[0];

  const [mode, setMode] = useState(() => localStorage.getItem("qsf-theme") || "dark");
  const T = THEMES[mode];
  useEffect(() => {
    injectStyles(mode);
    localStorage.setItem("qsf-theme", mode);
  }, [mode]);

  const [operator, setOperator] = useState("");
  const [message, setMessage] = useState({ text: "", ok: true });
  const [submitting, setSubmitting] = useState(false);
  const [editing, setEditing] = useState(false);

  const initialFormData = {
    DatePreparation: "",
    PhasePreparation: "",
    TemperatureBainCommanderie: "",
    MasseResinePreparee: "",
    MasseBain1Restant: "",
    NouvelleResineDansBain1Pct: "",
    ObservationsPreparation: "",
    DateControle: today,
    PhaseJournee: "",
    Operator: "",
    RefMesure: "",
    TemperatureTest: "",
    TypeResine: "",
    MasseResineFraicheAjoutee: "",
    RotorNumero: "",
    VitesseRpm: "",
    ViscositeMesuree: "",
    MasseAvantSechage: "",
    MasseApresSechage: "",
    MassePrelevee: "",
    TemperaturePicExotherm: "",
    TempsPicExothermMin: "",
    Observations: "",
    index: "",
  };

  const [formData, setFormData] = useState(initialFormData);

  useEffect(() => {
    const raw = localStorage.getItem("user");
    if (raw) {
      try {
        const u = JSON.parse(raw);
        setOperator(u.username || "");
      } catch {
        /* ignore */
      }
    }
  }, []);

  useEffect(() => {
    if (operator) setFormData((p) => ({ ...p, Operator: operator }));
  }, [operator]);

  useEffect(() => {
    const fromState = location.state?.item;
    if (fromState) {
      const flat = { ...fromState };
      setFormData((p) => ({ ...p, ...flat, index: flat.index ?? "" }));
      setEditing(true);
    }
  }, [location.state]);

  const extraitSecDisplay = useMemo(() => {
    const m1 = parseFloat(String(formData.MasseAvantSechage).replace(",", "."));
    const m2 = parseFloat(String(formData.MasseApresSechage).replace(",", "."));
    if (!Number.isFinite(m1) || !Number.isFinite(m2) || m1 <= 0) return "";
    return ((m1 - m2) / m1 * 100).toFixed(2);
  }, [formData.MasseAvantSechage, formData.MasseApresSechage]);

  const handleChange = (e) => {
    let { name, value, type } = e.target;
    if (type === "number" && Number(value) < 0) value = "";
    setFormData((s) => ({ ...s, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSubmitting(true);
    setMessage({ text: "", ok: true });
    try {
      const payload = { ...formData };
      if (!editing) delete payload.index;
      const r = await apiFetch(editing ? "/api/controle-resine/update" : "/api/controle-resine/add", {
        method: editing ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await r.json();
      setMessage({ text: result.message || "Terminé", ok: result.status === "success" });
      if (result.status === "success" && !editing) {
        setFormData({ ...initialFormData, DateControle: today, Operator: operator });
      }
    } catch {
      setMessage({ text: "Erreur réseau", ok: false });
    } finally {
      setSubmitting(false);
    }
  };

  const s = {
    page: { minHeight: "100vh", background: T.page.bg, color: T.page.color, fontFamily: "system-ui,sans-serif" },
    wrap: { padding: "20px 16px 48px", maxWidth: 900, margin: "0 auto" },
    topBar: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 },
    sectionTitle: { fontWeight: 900, fontSize: 18, textTransform: "uppercase", color: T.sectionTitle.color },
    card: { background: T.card.bg, border: `1px solid ${T.card.border}`, borderRadius: 16, overflow: "hidden", marginBottom: 20 },
    cardHeader: { padding: "12px 18px", background: T.cardHeader.bg, borderBottom: `1px solid ${T.cardHeader.border}`, fontWeight: 800, fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: T.cardHeader.color },
    formWrap: { padding: "8px 16px 16px" },
    input: { width: "100%", height: 36, background: T.input.bg, border: `1px solid ${T.input.border}`, borderRadius: 8, color: T.input.color, fontSize: 13, paddingLeft: 12, boxSizing: "border-box" },
    inputReadonly: { width: "100%", height: 36, background: T.inputReadonly.bg, border: `1px solid ${T.inputReadonly.border}`, borderRadius: 8, color: T.inputReadonly.color, fontSize: 13, paddingLeft: 12, boxSizing: "border-box" },
    select: { width: "100%", height: 36, background: T.select.bg, border: `1px solid ${T.select.border}`, borderRadius: 8, color: T.select.color, fontSize: 13, paddingLeft: 10, boxSizing: "border-box" },
    textarea: { width: "100%", minHeight: 72, background: T.input.bg, border: `1px solid ${T.input.border}`, borderRadius: 8, color: T.input.color, fontSize: 13, padding: 10, boxSizing: "border-box", resize: "vertical" },
    btnSave: { width: "100%", padding: "14px", background: T.accent, color: T.accentText, fontWeight: 900, fontSize: 13, border: "none", borderRadius: 10, cursor: "pointer" },
    toggleBtn: { background: T.toggleBtn.bg, border: `1px solid ${T.toggleBtn.border}`, color: T.toggleBtn.color, borderRadius: 10, padding: "8px 14px", cursor: "pointer" },
    msgOk: { color: "#4ade80", textAlign: "center", marginTop: 12, fontWeight: 700 },
    msgErr: { color: "#f87171", textAlign: "center", marginTop: 12, fontWeight: 700 },
  };

  const phases = [
    { value: "", label: "—" },
    { value: "AM", label: "Matin (AM)" },
    { value: "PM", label: "Après-midi (PM)" },
  ];

  return (
    <div style={s.page}>
      <MenuBar active="qualite" />
      <div style={s.wrap}>
        <div style={s.topBar}>
          <span style={s.sectionTitle}>Contrôle résine Biorez</span>
          <button type="button" style={s.toggleBtn} onClick={() => setMode((m) => (m === "dark" ? "light" : "dark"))}>
            {mode === "dark" ? "☀️ Clair" : "🌙 Sombre"}
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={s.card}>
            <div style={s.cardHeader}>Préparation commanderie</div>
            <div style={s.formWrap}>
              <Row label="Date préparation" T={T}>
                <input className="cresine-input" style={s.input} type="date" name="DatePreparation" value={formData.DatePreparation} onChange={handleChange} />
              </Row>
              <Row label="Phase" T={T}>
                <select className="cresine-select" style={s.select} name="PhasePreparation" value={formData.PhasePreparation} onChange={handleChange}>
                  {phases.map((p) => (
                    <option key={p.value || "empty"} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </Row>
              <Row label="Temp. bain commanderie (°C)" T={T}>
                <input className="cresine-input" style={s.input} type="number" step="any" name="TemperatureBainCommanderie" value={formData.TemperatureBainCommanderie} onChange={handleChange} />
              </Row>
              <Row label="Masse résine préparée (kg)" T={T}>
                <input className="cresine-input" style={s.input} type="number" step="any" name="MasseResinePreparee" value={formData.MasseResinePreparee} onChange={handleChange} />
              </Row>
              <Row label="Masse bain 1 restant (kg)" T={T}>
                <input className="cresine-input" style={s.input} type="number" step="any" name="MasseBain1Restant" value={formData.MasseBain1Restant} onChange={handleChange} />
              </Row>
              <Row label="Nouvelle résine dans bain-1 (%)" T={T}>
                <input className="cresine-input" style={s.input} type="number" step="any" name="NouvelleResineDansBain1Pct" value={formData.NouvelleResineDansBain1Pct} onChange={handleChange} />
              </Row>
              <Row label="Observations préparation" T={T}>
                <textarea className="cresine-input" style={s.textarea} name="ObservationsPreparation" value={formData.ObservationsPreparation} onChange={handleChange} />
              </Row>
            </div>
          </div>

          <div style={s.card}>
            <div style={s.cardHeader}>Mesures viscosité & qualité</div>
            <div style={s.formWrap}>
              <Row label="Date contrôle *" T={T}>
                <input className="cresine-input" style={s.input} type="date" name="DateControle" required value={formData.DateControle} onChange={handleChange} />
              </Row>
              <Row label="Phase journée *" T={T}>
                <select className="cresine-select" style={s.select} name="PhaseJournee" required value={formData.PhaseJournee} onChange={handleChange}>
                  <option value="">— choisir —</option>
                  {phases.filter((p) => p.value).map((p) => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </Row>
              <Row label="Opérateur *" T={T}>
                <input className="cresine-input" style={s.inputReadonly} readOnly name="Operator" value={formData.Operator} />
              </Row>
              <Row label="Réf. mesure" T={T}>
                <input className="cresine-input" style={s.input} name="RefMesure" value={formData.RefMesure} onChange={handleChange} placeholder="ex. 24/03/2026 -1" />
              </Row>
              <Row label="Temp. test (°C)" T={T}>
                <input className="cresine-input" style={s.input} type="number" step="any" name="TemperatureTest" value={formData.TemperatureTest} onChange={handleChange} />
              </Row>
              <Row label="Type résine" T={T}>
                <select className="cresine-select" style={s.select} name="TypeResine" value={formData.TypeResine} onChange={handleChange}>
                  <option value="">—</option>
                  <option value="Neuve">Neuve</option>
                  <option value="A">Additivée (A)</option>
                  <option value="/">Sans ajout (/)</option>
                </select>
              </Row>
              <Row label="Masse fraîche ajoutée (kg)" T={T}>
                <input className="cresine-input" style={s.input} type="number" step="any" name="MasseResineFraicheAjoutee" value={formData.MasseResineFraicheAjoutee} onChange={handleChange} />
              </Row>
              <Row label="Rotor n°" T={T}>
                <input className="cresine-input" style={s.input} name="RotorNumero" value={formData.RotorNumero} onChange={handleChange} />
              </Row>
              <Row label="Vitesse (rpm)" T={T}>
                <input className="cresine-input" style={s.input} type="number" step="any" name="VitesseRpm" value={formData.VitesseRpm} onChange={handleChange} />
              </Row>
              <Row label="Viscosité (mPa·s)" T={T}>
                <input className="cresine-input" style={s.input} type="number" step="any" name="ViscositeMesuree" value={formData.ViscositeMesuree} onChange={handleChange} />
              </Row>
              <Row label="Masse avant séchage (g)" T={T}>
                <input className="cresine-input" style={s.input} type="number" step="any" name="MasseAvantSechage" value={formData.MasseAvantSechage} onChange={handleChange} />
              </Row>
              <Row label="Masse après séchage (g)" T={T}>
                <input className="cresine-input" style={s.input} type="number" step="any" name="MasseApresSechage" value={formData.MasseApresSechage} onChange={handleChange} />
              </Row>
              <Row label="Extrait sec (%)" T={T}>
                <input className="cresine-input" style={s.inputReadonly} readOnly value={extraitSecDisplay} />
              </Row>
              <Row label="Masse prélevée (g)" T={T}>
                <input className="cresine-input" style={s.input} type="number" step="any" name="MassePrelevee" value={formData.MassePrelevee} onChange={handleChange} />
              </Row>
              <Row label="Pic exotherm (°C)" T={T}>
                <input className="cresine-input" style={s.input} type="number" step="any" name="TemperaturePicExotherm" value={formData.TemperaturePicExotherm} onChange={handleChange} />
              </Row>
              <Row label="Temps pic exotherm (min)" T={T}>
                <input className="cresine-input" style={s.input} type="number" step="any" name="TempsPicExothermMin" value={formData.TempsPicExothermMin} onChange={handleChange} />
              </Row>
              <Row label="Observations" T={T}>
                <textarea className="cresine-input" style={s.textarea} name="Observations" value={formData.Observations} onChange={handleChange} />
              </Row>
            </div>
          </div>

          <button type="submit" style={s.btnSave} disabled={submitting}>
            {submitting ? "Enregistrement…" : editing ? "Mettre à jour" : "Enregistrer"}
          </button>
          {message.text && (
            <div style={message.ok ? s.msgOk : s.msgErr}>{message.text}</div>
          )}
        </form>
      </div>
    </div>
  );
}
